// "Make review pack" (#719, UAT only): this option's Take Off, Summary and
// REVIEW as one PDF, made on the server from CRM and saved to the job's
// Proposal & Take-Off folder over the option's previous pack, for a reviewer
// to open. Shown only while Measure2Quote's portal status is uat (/api/me),
// so it goes by itself when the app goes live; the API checks it again.
//
// Like every generated file it lives only on SharePoint, opened from there in
// the tab opened on the tap (lib/file-handoff.ts).
import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { loadable } from 'jotai/utils';
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { Button } from '@higgins/ui';
import { ApiError, isConnectionFailure, makeReviewPackApi } from '../lib/api';
import { isoToday } from '../lib/dates';
import { openWaitingTab } from '../lib/file-handoff';
import { meAtom } from '../store/atoms';
import { SavedDocumentResult, type SavedResult } from './saved-document-result';

const meLoadable = loadable(meAtom);

function failure(err: unknown): string {
  if (isConnectionFailure(err)) return 'Making the review pack needs signal.';
  return err instanceof ApiError ? err.message : 'The review pack could not be made.';
}

interface Props {
  optionId: string;
  /** Why it can't be made right now (changes still syncing: it's made from what CRM has); null = it can. */
  blockedBecause: string | null;
}

export function ReviewPackPanel({ optionId, blockedBecause }: Props) {
  const me = useAtomValue(meLoadable);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SavedResult | null>(null);
  if (me.state !== 'hasData' || !me.data.reviewPack) return null;

  async function makePack() {
    setBusy(true);
    setResult(null);
    const tab = openWaitingTab('Making the review pack…');
    try {
      const pack = await makeReviewPackApi(optionId, isoToday());
      tab?.show(pack.webUrl);
      // The PDF is saved; a Word draft SharePoint wouldn't recycle is said, not treated as a failure.
      const draft = pack.draftLeft ? ` The Word draft “${pack.draftLeft.name}” couldn't be removed — delete it from Proposal & Take-Off.` : '';
      setResult({ ok: true, text: `${pack.fileName} — saved to the job's Proposal & Take-Off folder and ${tab ? tab.where : 'ready to open'}.${draft}`, url: pack.webUrl });
    } catch (err) {
      tab?.close();
      setResult({ ok: false, text: failure(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 border-t border-slate-200 pt-3">
      <h4 className="mb-1 font-medium text-slate-900">Review pack (UAT)</h4>
      <p className="mb-2 text-xs text-slate-500">This option&rsquo;s Take Off, Summary and REVIEW as one PDF for a reviewer. Making it again replaces the last one; SharePoint keeps the older copies.</p>
      <Button size="sm" variant="outline" onClick={() => void makePack()} disabled={busy || !!blockedBecause}>
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ClipboardCheck className="mr-1 h-4 w-4" />} {busy ? 'Making the review pack…' : 'Make review pack'}
      </Button>
      {blockedBecause && <p className="mt-1 text-xs text-slate-500">{blockedBecause}</p>}
      <SavedDocumentResult result={result} />
    </div>
  );
}
